/**
 * Sticky product info — keeps the PDP options in view on desktop.
 *
 * On desktop the column's blocks are wrapped in a sticky group
 * (`.sticky-info__group`) inside a track as tall as the gallery side, so they
 * stay pinned until the gallery side ends. The pinned part is the fewest
 * blocks, from the title through at least Optional Extras, that fill the
 * screen (with options closed): e.g. title to Optional Extras on divan bases,
 * title to delivery messaging on mattresses. Blocks after it sit below the
 * track and scroll normally; because the pinned part fills the screen, the
 * space between them is never visible. If no run of blocks fills the screen,
 * the whole column is pinned.
 * On mobile the blocks are unwrapped back to their original order.
 *
 * The sticky `top` offset is 20px, plus the header group's height when that is
 * sticky.
 *
 * Opening an option makes the pinned part taller, so the track grows by the
 * height of the open option panels (`.product-option-content`, 0px when
 * closed, animated on open and close) and the pinned range stays as long as
 * with every option closed.
 * While an option opens (including its expand animation) the page is also
 * scrolled back by any jump, so the options stay where they were and the
 * blocks below move down.
 */
(() => {
  const GAP = 20;
  const HOLD_MS = 900;
  const desktop = window.matchMedia('(min-width: 1024px)');
  const column = document.querySelector('.product-info.sticky-info--desktop');

  if (!column) return;

  const gallery = column.parentElement.querySelector('.product-carousel');
  const extras = column.querySelector(':scope > .product__block--product_optional_extras');
  const track = document.createElement('div');
  const group = document.createElement('div');
  const allBlocks = Array.from(column.children);
  const blocksToExtras = extras
    ? allBlocks.filter(
        (block) => block === extras || block.compareDocumentPosition(extras) & Node.DOCUMENT_POSITION_FOLLOWING
      )
    : [];

  track.className = 'sticky-info__track';
  group.className = 'sticky-info__group';
  track.append(group);

  let frame = null;

  const headerOffset = () => {
    const header = document.querySelector('.header-group-wrapper');
    if (!header || getComputedStyle(header).position !== 'sticky') return 0;

    return Math.max(0, header.getBoundingClientRect().bottom);
  };

  const pinnedTop = () => headerOffset() + GAP;

  const stickyElement = () => (group.isConnected ? group : column);

  const openPanelsHeight = () =>
    Array.from(
      column.querySelectorAll('.product__block--product_form .product-option-content'),
      (panel) => panel.offsetHeight
    ).reduce((total, height) => total + height, 0);

  /**
   * The fewest leading blocks (at least through Optional Extras) whose height,
   * with options closed, fills the screen; the whole column if none do.
   * @returns {HTMLElement[]}
   */
  const blocksToPin = () => {
    if (!blocksToExtras.length) return allBlocks;

    // Summed heights rather than positions, so the result is the same whether
    // or not the blocks are currently wrapped in the track.
    const gap = parseFloat(getComputedStyle(column).rowGap) || 0;
    const needed = window.innerHeight - GAP * 2 + openPanelsHeight();
    let height = 0;
    let visible = 0;

    for (let end = 0; end < allBlocks.length; end += 1) {
      const blockHeight = allBlocks[end].offsetHeight;
      if (!blockHeight) continue;

      height += blockHeight + (visible ? gap : 0);
      visible += 1;
      if (end >= blocksToExtras.length - 1 && height >= needed) return allBlocks.slice(0, end + 1);
    }

    return allBlocks;
  };

  const unwrap = () => {
    if (!group.isConnected) return;

    track.before(...group.children);
    track.remove();
    column.classList.remove('has-sticky-group');
  };

  const placeBlocks = () => {
    if (!desktop.matches) {
      unwrap();
      return;
    }

    const blocks = blocksToPin();
    if (group.isConnected && blocks.length === group.children.length) return;

    unwrap();
    blocks[0].before(track);
    group.append(...blocks);
    column.classList.add('has-sticky-group');
  };

  const update = () => {
    frame = null;

    if (!desktop.matches) {
      column.style.removeProperty('--sticky-info-top');
      track.style.removeProperty('min-height');
      return;
    }

    column.style.setProperty('--sticky-info-top', `${pinnedTop()}px`);
    if (gallery && group.isConnected) {
      track.style.minHeight = `${gallery.offsetHeight + openPanelsHeight()}px`;
    }
  };

  const requestUpdate = () => {
    if (frame === null) frame = requestAnimationFrame(update);
  };

  const onViewportChange = () => {
    placeBlocks();
    requestUpdate();
  };

  /**
   * Keeps the pinned options in place while their content grows.
   */
  const holdInPlace = () => {
    const pinned = stickyElement();
    if (!desktop.matches || pinned.dataset.stickyHold) return;

    const target = pinnedTop();
    if (Math.abs(pinned.getBoundingClientRect().top - target) > 2) return;

    const until = performance.now() + HOLD_MS;
    pinned.dataset.stickyHold = 'true';

    const tick = () => {
      const top = pinned.getBoundingClientRect().top;
      if (top < target - 0.5) window.scrollBy(0, top - target);

      if (performance.now() < until) {
        requestAnimationFrame(tick);
      } else {
        delete pinned.dataset.stickyHold;
      }
    };

    requestAnimationFrame(tick);
  };

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', onViewportChange);
  desktop.addEventListener('change', onViewportChange);

  if (gallery && 'ResizeObserver' in window) {
    const observer = new ResizeObserver(requestUpdate);
    observer.observe(gallery);
    observer.observe(group);
  }

  column.addEventListener('click', holdInPlace, true);
  column.addEventListener('toggle', holdInPlace, true);

  window.addEventListener('load', onViewportChange);

  placeBlocks();
  update();
})();
